/**
 * Determines if `data` is an Error.
 */
export function isError(data) {
    return (data !== null &&
        typeof data === 'object' &&
        typeof data.name === 'string' &&
        typeof data.message === 'string');
}
/**
 * Determines if `data` is a CustomError.
 */
export function isCustomError(data) {
    return (isError(data) &&
        (data.cause == null || isCustomError(data.cause)));
}
/**
 * Converts `error` to a JSON object.
 */
export function toJSON(error) {
    if (!isError(error)) {
        return {
            name: 'TypeError',
            message: 'Invalid "error" object.',
            error,
        };
    }
    const result = {
        ...error,
        name: error.name,
        message: error.message,
    };
    if (error.cause != null) {
        result.cause = toJSON(error.cause);
    }
    return result;
}
/**
 * Converts `error` to a CustomError instance created using the Error constructor.
 */
export function fromJSON(error) {
    if (!isError(error)) {
        return createError({
            name: 'TypeError',
            message: 'Invalid "error" object.',
            error,
        });
    }
    const result = createError({
        ...error,
        name: error.name,
        message: error.message,
        cause: undefined,
    });
    if (error.cause != null) {
        result.cause = fromJSON(error.cause);
    }
    return result;
}
/**
 * Creates a new Error instance with the specified properties.
 * Additionally, `cause.message` is automatically appended to the new error's `message`.
 */
export function createError(data = {}) {
    assert(data != null && typeof data === 'object', 'Argument "data" must be an object.');
    assert(typeof data.name === 'string' || data.name === undefined, 'Argument "data.name" must be a string or undefined.');
    assert(typeof data.message === 'string' || data.message === undefined, 'Argument "data.message" must be a string or undefined.');
    assert(isError(data.cause) || data.cause === undefined, 'Argument "data.cause" must be an Error or undefined.');
    const name = data.name;
    const cause = data.cause;
    const message = data.message
        ? cause
            ? `${data.message} => ${cause}`
            : data.message
        : cause
            ? `=> ${cause}`
            : '';
    const error = new Error(message);
    if (name !== undefined) {
        Object.defineProperty(error, 'name', {
            configurable: true,
            value: name,
            writable: true,
        });
    }
    for (const key in data) {
        if (data.hasOwnProperty(key) && key !== 'name' && key !== 'message') {
            error[key] = data[key];
        }
    }
    return error;
}
export function isSyncOtError(error) {
    return isCustomError(error) && /^SyncOtError($| )/.test(error.name);
}
export function createTsonError(message) {
    return createError({
        message,
        name: 'SyncOtError TSON',
    });
}
export function isTsonError(error) {
    return isCustomError(error) && error.name === 'SyncOtError TSON';
}
/**
 * Creates a new InvalidEntity error.
 * @param entityName The entity name.
 * @param entity The entity instance.
 * @param key The name of the invalid property. Pass in `null`, if the entire entity is invalid.
 */
export function createInvalidEntityError(entityName, entity, key = null) {
    assert(typeof entityName === 'string', 'Argument "entityName" must be a string.');
    assert(typeof key === 'string' || key === null, 'Argument "key" must be a string or null.');
    return createError({
        entity,
        entityName,
        key,
        message: key === null
            ? `Invalid "${entityName}".`
            : `Invalid "${entityName}.${key}".`,
        name: 'SyncOtError InvalidEntity',
    });
}
export function isInvalidEntityError(error) {
    return isCustomError(error) && error.name === 'SyncOtError InvalidEntity';
}
export function createTypeNotFoundError(typeName) {
    assert(typeof typeName === 'string', 'Argument "typeName" must be a string.');
    return createError({
        message: `Type "${typeName}" not found.`,
        name: 'SyncOtError TypeNotFound',
        typeName,
    });
}
export function isTypeNotFoundError(error) {
    return isCustomError(error) && error.name === 'SyncOtError TypeNotFound';
}
export function createNoServiceError(message) {
    return createError({
        message,
        name: 'SyncOtError NoService',
    });
}
export function isNoServiceError(error) {
    return isCustomError(error) && error.name === 'SyncOtError NoService';
}
export function createDisconnectedError(message) {
    return createError({
        message,
        name: 'SyncOtError Disconnected',
    });
}
export function isDisconnectedError(error) {
    return isCustomError(error) && error.name === 'SyncOtError Disconnected';
}
export function createNotInitializedError(message) {
    return createError({
        message,
        name: 'SyncOtError NotInitialized',
    });
}
export function isNotInitializedError(error) {
    return isCustomError(error) && error.name === 'SyncOtError NotInitialized';
}
export function createAlreadyInitializedError(message) {
    return createError({
        message,
        name: 'SyncOtError AlreadyInitialized',
    });
}
export function isAlreadyInitializedError(error) {
    return (isCustomError(error) && error.name === 'SyncOtError AlreadyInitialized');
}
export function createUnexpectedSessionIdError(message) {
    return createError({
        message,
        name: 'SyncOtError UnexpectedSessionId',
    });
}
export function isUnexpectedSessionIdError(error) {
    return (isCustomError(error) && error.name === 'SyncOtError UnexpectedSessionId');
}
export function createUnexpectedVersionNumberError(message) {
    return createError({
        message,
        name: 'SyncOtError UnexpectedVersionNumber',
    });
}
export function isUnexpectedVersionNumberError(error) {
    return (isCustomError(error) &&
        error.name === 'SyncOtError UnexpectedVersionNumber');
}
export function createUnexpectedSequenceNumberError(message) {
    return createError({
        message,
        name: 'SyncOtError UnexpectedSequenceNumber',
    });
}
export function isUnexpectedSequenceNumberError(error) {
    return (isCustomError(error) &&
        error.name === 'SyncOtError UnexpectedSequenceNumber');
}
export function createSessionError(message, cause) {
    return createError({
        cause,
        message,
        name: 'SyncOtError Session',
    });
}
export function isSessionError(error) {
    return isCustomError(error) && error.name === 'SyncOtError Session';
}
export function createPresenceError(message, cause) {
    return createError({
        cause,
        message,
        name: 'SyncOtError Presence',
    });
}
export function isPresenceError(error) {
    return isCustomError(error) && error.name === 'SyncOtError Presence';
}
export function createAuthError(message, cause) {
    return createError({
        cause,
        message,
        name: 'SyncOtError Auth',
    });
}
export function isAuthError(error) {
    return isCustomError(error) && error.name === 'SyncOtError Auth';
}
export function createDuplicateIdError(message) {
    return createError({
        message,
        name: 'SyncOtError DuplicateId',
    });
}
export function isDuplicateIdError(error) {
    return isCustomError(error) && error.name === 'SyncOtError DuplicateId';
}
export function createInvalidStreamError(message) {
    return createError({
        message,
        name: 'SyncOtError InvalidStream',
    });
}
export function isInvalidStreamError(error) {
    return isCustomError(error) && error.name === 'SyncOtError InvalidStream';
}
export function createSocketError(message, cause) {
    return createError({
        cause,
        message,
        name: 'SyncOtError Socket',
    });
}
export function isSocketError(error) {
    return isCustomError(error) && error.name === 'SyncOtError Socket';
}
export function createCompositeError(message, errors = []) {
    return createError({
        errors,
        message,
        name: 'SyncOtError Composite',
    });
}
export function isCompositeError(error) {
    return isCustomError(error) && error.name === 'SyncOtError Composite';
}
export function createAssertError(message) {
    return createError({
        message,
        name: 'SyncOtError Assert',
    });
}
export function isAssertError(error) {
    return isCustomError(error) && error.name === 'SyncOtError Assert';
}
export function createPingError(message, cause) {
    return createError({
        cause,
        message,
        name: 'SyncOtError Ping',
    });
}
export function isPingError(error) {
    return isCustomError(error) && error.name === 'SyncOtError Ping';
}
function assert(value, message) {
    if (!value) {
        throw createAssertError(message);
    }
}
